/**
 * AudiencesPage — the GA "Audiences" report (FEAT-ANALYTICS-002). A
 * "Total users by audience over time" chart + a metrics table (Total row +
 * one row per saved audience: total/new users, sessions, views/session, avg
 * session duration, revenue). The "+" on the Audience-name header builds a new
 * audience; row actions filter / edit / delete / push to a campaign.
 */
import React, { useCallback, useEffect, useState } from 'react';
import {
  Box, Flex, Text, Button, Table, Thead, Tbody, Tr, Th, Td, TableContainer, HStack,
  IconButton, Menu, MenuButton, MenuList, MenuItem, useToast, InputGroup, InputLeftElement, Input,
} from '@chakra-ui/react';
import { FiPlus, FiMoreVertical, FiEdit2, FiTrash2, FiFilter, FiSend, FiSearch } from 'react-icons/fi';
import { AudienceBuilder } from './AudienceBuilder';
import { listAudiences, fetchAudienceReport, deleteAudience, audienceToCampaign } from './audienceApi';
import { Panel, TrendChart, ReportFrame, fmtInt, fmtDuration } from '../components';
import { fmtMoney } from '../txnFormat';

export function useAudiences() {
  const [audiences, setAudiences] = useState([]);
  const [loading, setLoading] = useState(true);
  const reload = useCallback(async () => {
    setLoading(true);
    try { setAudiences(await listAudiences()); } catch (_) { setAudiences([]); } finally { setLoading(false); }
  }, []);
  useEffect(() => { reload(); }, [reload]);
  return { audiences, loading, reload };
}

const catsOf = (buckets = []) => buckets.map((b) => String(b || '').replace(/ 00:00:00$/, '').slice(5));
const m = (row) => row?.metrics || {};

function MetricCells({ metrics = {} }) {
  return (
    <>
      <Td isNumeric fontWeight="600" sx={{ fontVariantNumeric: 'tabular-nums' }}>{fmtInt(metrics.total_users)}</Td>
      <Td isNumeric sx={{ fontVariantNumeric: 'tabular-nums' }}>{fmtInt(metrics.new_users)}</Td>
      <Td isNumeric sx={{ fontVariantNumeric: 'tabular-nums' }}>{fmtInt(metrics.sessions)}</Td>
      <Td isNumeric sx={{ fontVariantNumeric: 'tabular-nums' }}>{Number(metrics.views_per_session || 0).toFixed(2)}</Td>
      <Td isNumeric sx={{ fontVariantNumeric: 'tabular-nums' }}>{fmtDuration(metrics.avg_session_seconds)}</Td>
      <Td isNumeric sx={{ fontVariantNumeric: 'tabular-nums' }}>{fmtMoney(metrics.revenue)}</Td>
    </>
  );
}

export function AudiencesReport({ params, onApplyFilter }) {
  const toast = useToast();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [builder, setBuilder] = useState({ open: false, initial: null });
  const [search, setSearch] = useState('');

  const key = JSON.stringify(params || {});
  const reload = useCallback(async () => {
    setLoading(true); setError(null);
    try { setData(await fetchAudienceReport(params || {})); }
    catch (e) { setError(e?.response?.data?.message || e.message); }
    finally { setLoading(false); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key]);
  useEffect(() => { reload(); }, [reload]);

  const onDelete = async (a) => {
    try { await deleteAudience(a.id); toast({ title: 'Audience deleted', status: 'info', duration: 1800 }); reload(); }
    catch (e) { toast({ title: 'Delete failed', status: 'error', duration: 3000 }); }
  };
  const onToCampaign = async (a) => {
    try { await audienceToCampaign(a.id); toast({ title: 'Campaign audience created', description: `${a.name} is now targetable in Campaigns`, status: 'success', duration: 4000 }); }
    catch (e) { toast({ title: 'Could not create campaign audience', description: e?.response?.data?.message || e.message, status: 'warning', duration: 5000 }); }
  };

  const rows = (data?.rows || []).filter((r) => !search || r.name.toLowerCase().includes(search.toLowerCase()));
  const chartSeries = (data?.rows?.length)
    ? data.rows.map((r) => ({ name: r.name, data: r.values || [] }))
    : [{ name: 'All users', data: data?.total?.values || [] }];

  return (
    <ReportFrame loading={loading} error={error}>
      {data && (
        <Box>
          <Panel title="Total users by audience over time">
            <TrendChart categories={catsOf(data.buckets)} series={chartSeries} />
          </Panel>

          <Box bg="white" borderWidth="1px" borderColor="#ebedf0" borderRadius="14px" boxShadow="0 1px 2px rgba(16,24,40,.04)" mt={4}>
            <Flex align="center" justify="space-between" p={4} gap={3} flexWrap="wrap" borderBottom="1px solid" borderColor="gray.100">
              <InputGroup size="sm" maxW="280px">
                <InputLeftElement pointerEvents="none"><FiSearch color="#94a3b8" /></InputLeftElement>
                <Input placeholder="Search audiences" value={search} onChange={(e) => setSearch(e.target.value)} borderRadius="8px" />
              </InputGroup>
              <Button leftIcon={<FiPlus />} size="sm" colorScheme="blue" onClick={() => setBuilder({ open: true, initial: null })}>New audience</Button>
            </Flex>

            <TableContainer>
              <Table size="sm" variant="simple">
                <Thead bg="gray.50">
                  <Tr>
                    <Th color="gray.500">
                      <HStack spacing={1}>
                        <Text>Audience name</Text>
                        <IconButton aria-label="Create audience" icon={<FiPlus />} size="2xs" variant="ghost" colorScheme="blue" onClick={() => setBuilder({ open: true, initial: null })} />
                      </HStack>
                    </Th>
                    <Th isNumeric color="gray.500">Total users</Th>
                    <Th isNumeric color="gray.500">New users</Th>
                    <Th isNumeric color="gray.500">Sessions</Th>
                    <Th isNumeric color="gray.500">Views / session</Th>
                    <Th isNumeric color="gray.500">Avg. session duration</Th>
                    <Th isNumeric color="gray.500">Total revenue</Th>
                    <Th></Th>
                  </Tr>
                </Thead>
                <Tbody>
                  <Tr bg="gray.50" fontWeight="700">
                    <Td>Total</Td>
                    <MetricCells metrics={data.total?.metrics} />
                    <Td></Td>
                  </Tr>
                  {rows.map((a) => (
                    <Tr key={a.id} _hover={{ bg: 'gray.50' }}>
                      <Td>
                        <HStack>
                          <Box w="10px" h="10px" borderRadius="full" bg={a.color || 'var(--brand-primary)'} />
                          <Text as="button" fontWeight="600" color="var(--brand-primary)" onClick={() => onApplyFilter && onApplyFilter(a)} _hover={{ textDecoration: 'underline' }}>{a.name}</Text>
                        </HStack>
                      </Td>
                      <MetricCells metrics={m(a)} />
                      <Td isNumeric>
                        <Menu>
                          <MenuButton as={IconButton} size="xs" variant="ghost" icon={<FiMoreVertical />} aria-label="more" />
                          <MenuList>
                            <MenuItem icon={<FiFilter />} onClick={() => onApplyFilter && onApplyFilter(a)}>Apply as filter</MenuItem>
                            <MenuItem icon={<FiEdit2 />} onClick={() => setBuilder({ open: true, initial: a })}>Edit</MenuItem>
                            <MenuItem icon={<FiSend />} onClick={() => onToCampaign(a)}>Create campaign audience</MenuItem>
                            <MenuItem icon={<FiTrash2 />} color="red.500" onClick={() => onDelete(a)}>Delete</MenuItem>
                          </MenuList>
                        </Menu>
                      </Td>
                    </Tr>
                  ))}
                  {rows.length === 0 && (
                    <Tr>
                      <Td colSpan={8}>
                        <Flex direction="column" align="center" py={10} gap={2}>
                          <Text color="gray.500" fontSize="sm">No audiences yet — build one to compare it against all users.</Text>
                          <Button leftIcon={<FiPlus />} size="sm" variant="outline" onClick={() => setBuilder({ open: true, initial: null })}>Build your first audience</Button>
                        </Flex>
                      </Td>
                    </Tr>
                  )}
                </Tbody>
              </Table>
            </TableContainer>
          </Box>

          <AudienceBuilder isOpen={builder.open} initial={builder.initial} onClose={() => setBuilder({ open: false, initial: null })} onSaved={reload} />
        </Box>
      )}
    </ReportFrame>
  );
}

export default AudiencesReport;
